import React from 'react';
import {
  string, number, arrayOf, shape
} from 'prop-types';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Text
} from 'recharts';
import { Translate } from 'react-localize-redux';

import { variableStatsType, variableTypeValues } from '../../constants/types';
import { getTranslation, withLang, langType } from '../../i18n';
import variables from '../../styles/variables.json';

import styles from './variableRadarSummary.module.css';
import { toLocaleNumber } from '../../util';

const { blue, orange } = variables;

const supportedVariableTypes = [variableTypeValues.RELATIVE];

const formatVariableData = (variableStats, lang) => variableStats.map((s) => {
  const translatedName = getTranslation(s.name, lang);

  return {
    name: translatedName,
    average: s.average,
    userAverage: s.userAverage
  };
});

const commonLabelPropTypes = {
  x: number.isRequired,
  y: number.isRequired,
  payload: shape({
    value: number.isRequired
  }).isRequired
};

const VariableNameLabel = (labelProps) => (
  <Text
    className={styles.variableNameLabel}
    width={100}
    textAnchor={labelProps.textAnchor}
    x={labelProps.x}
    y={labelProps.y}
  >
    { labelProps.payload.value }
  </Text>
);

VariableNameLabel.propTypes = {
  textAnchor: string.isRequired, ...commonLabelPropTypes // eslint-disable-line react/no-unused-prop-types
};

const LABEL_HORIZONTAL_OFFSET = 8;
const LABEL_VERTICAL_OFFSET = 10;
const RadiusAxisLabel = (props) => (
  <Text
    textAnchor="start"
    verticalAnchor="start"
    x={props.x + LABEL_HORIZONTAL_OFFSET}
    y={props.y + LABEL_VERTICAL_OFFSET}
  >
    { props.payload.value !== 0 ? props.payload.value : '' }
  </Text>
);

RadiusAxisLabel.propTypes = commonLabelPropTypes;

const RadarLegend = () => (
  <div className={styles.legendContainer}>
    <div className={styles.legend}>
      <span className={styles.legendOwn}><Translate id="surveySummary.userAverage" /></span>
      <span className={styles.legendGroup}><Translate id="surveySummary.groupAverage" /></span>
    </div>
    <div><p><Translate id="surveySummary.value1Explanation" /></p></div>
    <div><p><Translate id="surveySummary.value5Explanation" /></p></div>
  </div>
);

const getRadarTickCount = (variableStats) => {
  const values = variableStats[0].responseOptionValues;
  return values.length + (values[0] === 0 ? 0 : 1);
};

const VariableRadarSummary = (props) => {
  const variableStats = props.variableStats.filter((v) =>
    supportedVariableTypes.includes(v.type));
  if (!variableStats.length) {
    return null;
  }
  const tickCount = getRadarTickCount(variableStats);
  const domain = [0, tickCount - 1];
  const radarDotProps = { fillOpacity: 1 };
  const data = formatVariableData(variableStats, props.lang);

  return (
    <div className={styles.container}>
      <figure aria-label={props.translate('surveySummary.radarChartExplanation')}>
        <div className={styles.chart} aria-hidden>
          <ResponsiveContainer height="110%" debounce={300}>
            <RadarChart
              data={data}
              margin={{ right: 85, left: 85 }}
            >
              <Radar dataKey="average" fill={blue} fillOpacity={0.5} dot={radarDotProps} />
              <Radar dataKey="userAverage" fill={orange} fillOpacity={0.5} dot={radarDotProps} />
              <PolarGrid />
              <PolarAngleAxis dataKey="name" tick={VariableNameLabel} />
              <PolarRadiusAxis
                angle={90}
                domain={domain}
                tickCount={tickCount}
                tick={RadiusAxisLabel}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <figcaption>
          <ul className={styles.figureDescription}>
            {data.map(({ name, userAverage, average }) => (
              <li key={name}>
                <Translate
                  id="surveySummary.radarChartItemDescription"
                  data={{
                    name,
                    userAverage: toLocaleNumber(userAverage),
                    groupAverage: toLocaleNumber(average)
                  }}
                />
              </li>
            ))}
          </ul>
        </figcaption>
      </figure>
      <RadarLegend />
    </div>
  );
};

VariableRadarSummary.propTypes = {
  variableStats: arrayOf(variableStatsType).isRequired,
  ...langType
};

export default withLang(VariableRadarSummary);
